import '@mux/mux-uploader';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button, SCOPES } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';

import { ENUMS_VIDEO, MUX_AUTO_GENERATED_LANGUAGES } from '../../constants';
import videoTypes from '../../types';
import { createMuxStaticUpload } from '../../utils';

const propTypes = {

	/**
	 * The video which is awaiting upload, if any.
	 */
	video: videoTypes.video,

	/**
	 * The options for the upload configuration, if any.
	 */
	options: PropTypes.shape({

		/**
		 * The configuration for subtitles to be auto-generated, if any.
		 */
		generated_subtitles: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The BCP 47 language code of the closed caption stream. Defaults to en.
			 */
			language_code: PropTypes.oneOf(
				MUX_AUTO_GENERATED_LANGUAGES
			)

		}))

	}),

	/**
	 * The type of scope for the video.
	 */
	scope: PropTypes.oneOf([
		SCOPES.SESSION,
		SCOPES.ORGANIZATION,
	]).isRequired,

	/**
	 * The scope ID for the video.
	 */
	scopeId: PropTypes.string.isRequired,

	/**
	 * Callback function to execute when a video is successfully created.
	 * Function should accept the newly created video for parsing.
	 */
	onCreateVideo: PropTypes.func,

};

/**
 * Displays the Mux Uploader webcomponent used for uploading static
 * videos to Mux. Has a file picker and drag and drop. Upon request,
 * creates a new video record in the Library and returns the new video
 * containing the upload URL. The URL is then supplied as an endpoint
 * to the webcomponent for uploading the selected video file to Mux.
 * Mux handles chunking the video, displaying progress updates, handling
 * retries, providing errors, and more.
 *
 * Documentation can be found here:
 * https://docs.mux.com/guides/video/mux-uploader
 *
 * @param {Object} props  The component props.
 * @returns {Object}      The component.
 */
const MuxStaticUploader = (props) => {

	const {
		video,
		scope,
		scopeId,
		options,
		onCreateVideo,
	} = props;

	// The video currently being uploaded
	const [uploadVideo, setUploadVideo] = useState();

	/**
	 * When the file picker is opened, create a new library video
	 * Set the upload URL for the mux-uploader to use as the endpoint
	 * Send the video to the onCreateVideo callback for processing
	 */
	const onUpload = () => {
		if (video?.status === ENUMS_VIDEO.STATUS.WAITING && video?.upload_url) {
			setUploadVideo(video);
		} else {
			createMuxStaticUpload(scope, scopeId, options)
				.then(({ data }) => setUploadVideo(data))
				.catch((error) => console.error(error));
		}
	};

	/**
	 * Add event listener for the mux-uploader webcomponent to
	 * execute the onCreateVideo function when the video upload
	 * completes
	 */
	useEffect(() => {

		if (!onCreateVideo || !uploadVideo) { return () => {}; }

		const muxUploaderElement = document.querySelector('mux-uploader');
		const eventType = 'success';
		const listenerFunction = () => onCreateVideo(uploadVideo);

		muxUploaderElement.addEventListener(eventType, listenerFunction);

		return () => {
			muxUploaderElement.removeEventListener(eventType, listenerFunction);
		};

	}, [uploadVideo, onCreateVideo]);

	return (
		<mux-uploader
			endpoint={uploadVideo?.upload_url}
			no-drop>
			<Button
				color="neutral"
				slot="file-select"
				outline
				onClick={onUpload}>
				<FormattedMessage
					{...messages.TC_COMPONENTS_VIDEO_LIBRARY_UPLOADER_FILE_PICKER_BUTTON_LABEL} />
			</Button>
		</mux-uploader>
	);

};

MuxStaticUploader.propTypes = propTypes;

export default MuxStaticUploader;
